import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { Controller } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import type { ObservedMetadataKeyDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  METADATA_FILTER_KEY_MAX_CHARS,
  METADATA_FILTER_VALUE_MAX_CHARS,
} from '@/Domain/api/Export/export.dto';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/Domain/components/Shared/Accordion/Accordion';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import type { ExportAnalyticsForm } from '@/Domain/hooks/Conversation/useExportAnalyticsForm.hook';

const NO_METADATA_FILTER = '__all_conversations__';

type Props = {
  form: UseFormReturn<ExportAnalyticsForm>;
};

export const ConversationExportMetadataFilter = ({ form }: Props) => {
  const { t } = useTranslation('inbox', { keyPrefix: 'export.form' });
  const metadataKey = form.watch('metadata_key');
  const valueError = form.formState.errors.metadata_value;
  const { data: observedKeys } = useQuery<ObservedMetadataKeyDto[]>({
    queryKey: [QueryKey.actions.basic.observed_metadata_keys],
  });
  const keyOptions = (observedKeys ?? []).filter(
    (entry) =>
      !entry.key.startsWith('_') &&
      entry.key.length <= METADATA_FILTER_KEY_MAX_CHARS &&
      (entry.has_scalar_value ?? entry.example_value !== null),
  );
  const selectedKey = keyOptions.find((entry) => entry.key === metadataKey);

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="advanced_filtering" className="rounded-xl border border-border px-3">
        <AccordionTrigger
          className="cursor-pointer py-3 text-foreground"
          data-testid="conversation-export-metadata-filter-trigger"
        >
          {/* min-h-6 matches the badge's box, so the row keeps its height whether or not a field is set. */}
          <span className="flex min-h-6 items-center gap-2">
            <SlidersHorizontal className="size-4" />
            {t('metadata_filter_title')}
            {/* A filter left set while the section is closed still applies, so name it here. */}
            {metadataKey && (
              <Badge
                variant="secondary"
                size="md"
                data-testid="conversation-export-metadata-filter-active"
              >
                {metadataKey}
              </Badge>
            )}
          </span>
        </AccordionTrigger>
        <AccordionContent className="pb-3 pt-0">
          <FieldGroup className="gap-3">
            <p className="text-sm text-muted-foreground">{t('metadata_filter_description')}</p>
            <div className="space-y-1.5">
              <div className="grid gap-3 sm:grid-cols-2">
                <Controller
                  control={form.control}
                  name="metadata_key"
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="conversation-export-metadata-key">
                        {t('metadata_key')}
                      </FieldLabel>
                      <Select
                        value={field.value ?? NO_METADATA_FILTER}
                        onValueChange={(value) => {
                          field.onChange(value === NO_METADATA_FILTER ? undefined : value);
                          form.setValue('metadata_value', undefined);
                          form.clearErrors('metadata_value');
                        }}
                      >
                        <SelectTrigger
                          id="conversation-export-metadata-key"
                          className="w-full"
                          aria-invalid={fieldState.invalid}
                          data-testid="conversation-export-metadata-key"
                        >
                          {/* Names the field only: an example here reads as the value already chosen. */}
                          <SelectValue>{field.value ?? t('metadata_key_none')}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value={NO_METADATA_FILTER}
                            data-testid="conversation-export-metadata-key-none"
                          >
                            {t('metadata_key_none')}
                          </SelectItem>
                          {keyOptions.map((entry) => (
                            <SelectItem
                              key={entry.key}
                              value={entry.key}
                              data-testid={`conversation-export-metadata-key-${entry.key}`}
                            >
                              <span className="flex items-baseline gap-2">
                                <span>{entry.key}</span>
                                {entry.example_value && (
                                  <span className="text-xs font-normal text-muted-foreground">
                                    {t('metadata_value_example', { value: entry.example_value })}
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  )}
                />
                <Controller
                  control={form.control}
                  name="metadata_value"
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="conversation-export-metadata-value">
                        {t('metadata_value')}
                      </FieldLabel>
                      <Input
                        {...field}
                        id="conversation-export-metadata-value"
                        value={field.value ?? ''}
                        disabled={!metadataKey}
                        maxLength={METADATA_FILTER_VALUE_MAX_CHARS}
                        error={fieldState.invalid}
                        placeholder={t('metadata_value_placeholder')}
                        data-testid="conversation-export-metadata-value"
                      />
                    </Field>
                  )}
                />
              </div>
              {/* One reserved line, so choosing a field never resizes the dialog. */}
              <div className="min-h-5">
                {valueError ? (
                  <FieldError errors={[valueError]} />
                ) : (
                  selectedKey?.example_value && (
                    <FieldDescription className="pb-0">
                      {t('metadata_value_example', { value: selectedKey.example_value })}
                    </FieldDescription>
                  )
                )}
              </div>
            </div>
          </FieldGroup>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
